import { useState, useEffect, useCallback } from 'react';
import {
  getAuthSession,
  signInWithEmail,
  signUpWithEmail,
  signInWithOAuth,
  signOut,
  subscribeAuthState,
} from '../api/authApi';

export function useAuth() {
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    async function initAuth() {
      setIsLoading(true);
      const { session: curSession, user: curUser, error: sessionErr } =
        await getAuthSession();

      if (isMounted) {
        if (sessionErr) setError(sessionErr.message);
        setSession(curSession);
        setUser(curUser);
        setIsLoading(false);
      }
    }

    initAuth();

    const { unsubscribe } = subscribeAuthState((_event, newSession) => {
      if (isMounted) {
        setSession(newSession);
        setUser(newSession?.user || null);
        setIsLoading(false);
      }
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, []);

  const login = useCallback(async ({ email, password }) => {
    setError(null);
    const { data, error: err } = await signInWithEmail({ email, password });
    if (err) {
      setError(err.message);
      return { success: false, error: err };
    }
    return { success: true, data };
  }, []);

  const signup = useCallback(async ({ email, password, name }) => {
    setError(null);
    const { data, error: err } = await signUpWithEmail({ email, password, name });
    if (err) {
      setError(err.message);
      return { success: false, error: err };
    }
    return { success: true, data };
  }, []);

  const loginWithOAuth = useCallback(async ({ provider }) => {
    setError(null);
    const { data, error: err } = await signInWithOAuth({ provider });
    if (err) {
      setError(err.message);
      return { success: false, error: err };
    }
    return { success: true, data };
  }, []);

  const logout = useCallback(async () => {
    setError(null);
    const { error: err } = await signOut();
    if (err) {
      setError(err.message);
      return { success: false, error: err };
    }
    setSession(null);
    setUser(null);
    return { success: true };
  }, []);

  return {
    session,
    user,
    isAuthenticated: Boolean(user),
    isLoading,
    error,
    login,
    signup,
    loginWithOAuth,
    logout,
    clearError: useCallback(() => setError(null), []),
  };
}
